import type { AbstractCtor, RecordKey } from '../identity/core.ts';
import { isPresent, type Objectish, type TypeGuard } from '../identity/type_guards.ts';
import type { DeferredMessage } from './core.ts';
import { check, checkFail } from './core.ts';
import { logTypeName, logValue } from './logging.ts';

export interface ValueAssertion<V = unknown> {
  (v: V): boolean;
  explain?: (v: V) => string;
}

export function valueAssertion<V = unknown>(
  test: (v: V) => boolean,
  explain?: (v: V) => string,
): ValueAssertion<V> {
  return isPresent(explain) ? Object.assign(test, { explain }) : test;
}

// export function typeGuardBuilder<P, T extends V, V = unknown>(
//   test: (param: P, v: V) => boolean,
//   explain?: (param: P, v: V) => string,
// ) {
//   function build(param: P, v?: V) {
//     if (isPresent(v)) {
//       return test(param, v);
//     }
//     const curried = ((v2: V) => test(param, v2)) as TypeGuard<T, V>;
//     return isPresent(explain) ? Object.assign(curried, { explain }) : curried;
//   }
//   return build;
// }

// function isMemberOf<T extends string>(values: readonly T[]): TypeGuard<T>;
// function isMemberOf<T extends string>(values: readonly T[], v: unknown): v is T;
// function isMemberOf<T extends string>(values: readonly T[], v?: unknown): TypeGuard<T> | boolean {
//   return isPresent(v) ? values.includes(v as T) : (v: unknown) => isMemberOf(values, v);
//   // return values.includes(v as T);
// }

// const foo = isMemberOf(['a', 'b'] as const);
// const bar: string = 'c';
// if (isMemberOf(['a', 'b'] as const, bar)) {
//   const baz = bar;
// }

// function buildTypeGuard<P, T extends V, V, F extends (p: P, v: V) => v is T>(test: F) {
//   function curry(param: P): TypeGuard<T, V> {
//     return (v: V) => test(param, v);
//   }
//   return Object.assign(test, { curry });
// }

// // const isMemberOf2 = buildTypeGuard(<T>(v: unknown, values: readonly T[]): v is T => values.includes(v as T));
// const isMemberOf3 = buildTypeGuard(isMemberOf);
// const x = isMemberOf3(['x', 'y'], 'a');
// const y = isMemberOf3(['x', 'y']);

export function checkValid<T extends V, V = unknown>(
  value: V,
  guard: TypeGuard<T, V>,
  ...messages: DeferredMessage[]
): T {
  if (!guard(value)) {
    const msgs = [...messages];
    if (isPresent(guard.explain)) {
      msgs.push('failed condition:', guard.explain(value));
    } else {
      msgs.push(`failed on (value: ${logTypeName(value)})`);
    }
    checkFail('checkValid', ...msgs);
  }
  return value;
}

// TODO: Rename something other than assert since it does a runtime check.
export function assertValid<T extends V, V = unknown>(
  value: V,
  guard: TypeGuard<T, V>,
  ...messages: DeferredMessage[]
): asserts value is T {
  if (!guard(value)) {
    checkFail('assertValid failed.', ...messages);
  }
}

// Is there a more idiomatic way to assert the type of a value that is unknown to TS?
export function assertType<T>(value: unknown): asserts value is T {
  // This is an pure assertion without a no runtime component.
}

export function assertKeyOf<T extends Objectish>(
  value: RecordKey,
  obj: T,
): asserts value is keyof T {
  if (!(value in obj)) {
    checkFail(() => `assertKeyOf: ${String(value)} not in [${Object.keys(obj).join(',')}]`);
  }
}

// Dies if |expression()| does not throw. Also rethrows any value that is not of type |exception|.
export function checkThrows(
  exception: AbstractCtor<Error>,
  expression: () => void,
  ...messages: DeferredMessage[]
): void {
  try {
    expression();
    checkFail('checkThrows did not throw', ...messages);
  } catch (error) {
    if (!(error instanceof exception)) {
      console.error(
        `checkThrows caught ${logTypeName(error)} but expected ${exception.name}`,
        ...messages,
      );
      throw error;
    }
  }
}

// Dies unless exactly one of |expressions| is false.
// TODO: Accept truthy expressions instead of just boolean? Maybe just T|undefined?
export function checkExactlyOneOf(expressions: boolean[], ...messages: DeferredMessage[]): void {
  if (expressions.reduce((count, exp) => count + (exp ? 1 : 0), 0) !== 1) {
    checkFail('checkExactlyOneOf', ...messages);
  }
}

// Dies unless exactly one of |expressions| is false.
export function checkAtMostOneOf(expressions: boolean[], ...messages: DeferredMessage[]): void {
  const count = expressions.reduce((c, exp) => c + (exp ? 1 : 0), 0);
  if (count > 1) {
    checkFail(`checkAtMostOneOf(${expressions.length}) === ${count}`, ...messages);
  }
}

export function checkSingle<T>(value: T[] | undefined, ...messages: DeferredMessage[]): T {
  if (value?.length !== 1) {
    checkFail('checkSingle', ...messages);
  }
  return value[0];
}

export function autoLabel(value: string | number): string {
  if (typeof value === 'number') {
    return String(value);
  }
  // TODO: Second argument to replace should be ' $1', but that isn't working.
  const spaced = value.replace(/[A-Z]/g, (m) => ` ${m}`);
  return spaced.charAt(0).toLocaleUpperCase() + spaced.slice(1);
}

export function checkEqual<Actual, Expected extends Actual>(
  value: Actual,
  expected: Expected,
  ...messages: DeferredMessage[]
): Actual {
  if (value !== expected) {
    return checkFail(`checkEqual ${logValue(value)} !== ${logValue(expected)}`, ...messages);
  }
  return value;
}

export function checkEquivalent<Actual, Expected extends Actual>(
  value: Actual,
  expected: Expected,
  ...messages: DeferredMessage[]
): Actual {
  const lv = logValue(value, { expandIterable: true });
  const ev = logValue(expected, { expandIterable: true });
  if (lv !== ev) {
    return checkFail(`checkEqual ${lv} !== ${ev}`, ...messages);
  }
  return value;
}

// eslint-disable-next-line @typescript-eslint/naming-convention
export const assertEqual: <Actual, Expected extends Actual>(
  value: Actual,
  expected: Expected,
  ...messages: DeferredMessage[]
) => asserts value is Expected = checkEqual;

export function checkNotEqual<T>(value: T, expected: T, ...messages: DeferredMessage[]): T {
  if (value === expected) {
    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    return checkFail(`checkNotEqual ${value} === ${expected}`, ...messages);
  }
  return value;
}

export function checkClose<Actual, _Expected extends Actual>(
  value: number,
  expected: number,
  ...messages: DeferredMessage[]
): number {
  const digits = 10;
  if (Math.round(value * digits) !== Math.round(expected * digits)) {
    return checkFail(
      `checkClose ${logValue(value)} !== ${logValue(expected)} within ${digits} digits.`,
      ...messages,
    );
  }
  return value;
}

export function checkGreater(
  value: number,
  lowerExclusiveBound: number,
  ...messages: DeferredMessage[]
): number {
  if (value <= lowerExclusiveBound) {
    return checkFail(`checkGreater ${value} <= ${lowerExclusiveBound}`, ...messages);
  }
  return value;
}

export function checkGreaterOrEqual(
  value: number,
  lowerInclusiveBound: number,
  ...messages: DeferredMessage[]
): number {
  if (value < lowerInclusiveBound) {
    return checkFail(`checkGreaterOrEqual ${value} < ${lowerInclusiveBound}`, ...messages);
  }
  return value;
}

export function checkLess(
  value: number,
  upperExclusiveBound: number,
  ...messages: DeferredMessage[]
): number {
  if (value >= upperExclusiveBound) {
    return checkFail(`checkLess ${value} >= ${upperExclusiveBound}`, ...messages);
  }
  return value;
}

export function checkLessOrEqual(
  value: number,
  upperInclusiveBound: number,
  ...messages: DeferredMessage[]
): number {
  if (value > upperInclusiveBound) {
    return checkFail(`checkLessOrEqual ${value} > ${upperInclusiveBound}`, ...messages);
  }
  return value;
}

// Dies if |value| is not between |min| and |max|, inclusive.
export function checkRange(
  value: number,
  min: number,
  max: number,
  ...messages: DeferredMessage[]
): number {
  if (value < min || value > max) {
    return checkFail(`checkRange ${min} <= ${value} <= ${max}`, ...messages);
  }
  return value;
}

// Dies if |value| is not a valid index into |array|.
export function checkIndex(
  value: number,
  array: unknown[],
  ...messages: DeferredMessage[]
): number {
  return checkRange(value, 0, array.length - 1, ...messages);
}

// Dies if |value| is not a member of |array|.
export function checkOneOf<T>(value: T, array: T[], ...messages: DeferredMessage[]): T {
  if (!array.includes(value)) {
    return checkFail('checkOneOf', ...messages);
  }
  return value;
}

// Dies if |value| is a member of |array|.
export function checkNotOneOf<T>(value: T, array: T[], ...messages: DeferredMessage[]): T {
  if (array.includes(value)) {
    return checkFail('checkNotOneOf', ...messages);
  }
  return value;
}

export function checkNext<T>(iterable: IterableIterator<T>): T {
  const result = iterable.next();
  check(!(result.done ?? false), () => 'Iterator has no next element.');
  return result.value as T;
}
